import axios from "axios";
import * as Yup from "yup";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ccLogo } from "../../assets/data/Assests";
import BeatLoader from "react-spinners/BeatLoader";
import { REACT_APP_MAIN_URL } from "../../services/MainUrl";
import { HiEyeSlash, HiMiniEye, HiCheck } from "react-icons/hi2";
import { AccountProfile } from "../../redux/ApiSlice/AccountProfileSlice";
import { setClearHomepageVideos } from "../../redux/ApiSlice/HomePageVideoSlice";
import { setAuthData } from "../../redux/ApiSlice/Auth/AuthSlice";
import { initiateAppleLogin } from "../../services/appleAuth";
import {
    isPhoneIdentifierValue,
    phoneLocalPart,
    resolveEmailOrPhoneInput,
    formatSavedIdentifier,
    normalizeIdentifier,
} from "../../utils/phoneInput";
import { saveUserPreferences } from "../../utils/userPreferences";

const AppleIcon = () => (
    <svg className="inline h-4 w-4 mr-2 mb-0.5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
);

const loginSchema = Yup.object().shape({
    email: Yup.string()
        .required("Email or phone number required")
        .test("email-or-phone", "Enter a valid email or phone number", (value) => {
            if (!value) return false;
            const isEmail = /\S+@\S+\.\S+/.test(value);
            const isPhone = /^\+?[\d\s\-()]{7,20}$/.test(value);
            return isEmail || isPhone;
        }),
    password: Yup.string().required("Please Enter your password"),
});

const LoginPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [keepMeLogin, setKeepMeLogin] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const locationData = useRef(null);

    useEffect(() => {
        if (!navigator.geolocation) return;
        if (localStorage.getItem("locationPermission") !== "allowed") return;
        navigator.geolocation.getCurrentPosition(
            (position) => {
                locationData.current = {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                };
            },
            () => {
                locationData.current = null;
            },
            { timeout: 10000, maximumAge: 60000 }
        );
    }, []);

    const handleGetData = async () => {
        try {
            const response = await dispatch(AccountProfile());
            saveUserPreferences({
                enableAutoplay: response.payload?.autoplay ?? true,
                hideOwnLikeCount: false,
                muteVideosByDefault: response.payload?.mute_video ?? false,
            });
            navigate("/");
        } catch (error) {
            console.error("Error fetching account profile:", error);
            setIsLoading(false);
        }
    };

    const formik = useFormik({
        initialValues: {
            email: "",
            password: "",
        },
        validationSchema: loginSchema,
        onSubmit: async (values) => {
            setErrorMessage("");
            setIsLoading(true);

            const payload = { email: normalizeIdentifier(values.email), password: values.password };
            const locationPref = localStorage.getItem("locationPermission");
            if (locationData.current && locationPref !== "blocked") {
                payload.latitude = locationData.current.latitude;
                payload.longitude = locationData.current.longitude;
                payload.permission_type = locationPref === "allowed" ? "while_using" : "once";
            }

            try {
                const response = await axios({
                    method: "POST",
                    url: `${process.env.REACT_APP_MAIN_URL}api/oauth/login/`,
                    data: payload,
                    headers: { "Content-Type": "application/json" },
                    withCredentials: true,
                });

                const { data, status } = response;
                if (status === 200) {
                    dispatch(setClearHomepageVideos());

                    if (keepMeLogin) {
                        localStorage.setItem("email", payload.email);
                        localStorage.setItem("keepMeLogin", "true");
                    } else {
                        localStorage.removeItem("email");
                        localStorage.setItem("keepMeLogin", "false");
                    }

                    dispatch(setAuthData({
                        access: data?.access,
                        refresh: data?.refresh,
                        session_key: data?.session_key,
                        browser_group_id: data?.browser_group_id,
                    }));
                    // localStorage.setItem("accessToken", data?.access);
                    // localStorage.setItem("refreshToken", data?.refresh);
                    localStorage.setItem("new_User", "true");
                    toast.success("Successfully logged in!");
                    await handleGetData();
                }
            } catch (error) {
                const errMsg = error.response?.data?.error || "Something went wrong, please try again.";
                setErrorMessage(errMsg);
                setIsLoading(false);
            }
        },
    });

    const handleChangeKeepLogin = () => {
        setKeepMeLogin(!keepMeLogin);
    };

    const { setFieldValue } = formik;

    useEffect(() => {
        const savedEmail = localStorage.getItem("email");
        if (savedEmail) {
            setFieldValue("email", formatSavedIdentifier(savedEmail));
            setKeepMeLogin(true);
        }
    }, [setFieldValue]);

    // e.target.value here is the LOCAL input's own text — it never contains "+1" (that's a
    // static sibling span, see the JSX), so there's no prefix for the caret to get lost inside.
    const handleEmailOrPhoneChange = (e) => {
        const oldLocal = phoneLocalPart(formik.values.email);
        formik.setFieldValue("email", resolveEmailOrPhoneInput(oldLocal, e.target.value));
    };

    useEffect(() => {
        return () => {
            toast.dismiss();
        };
    }, []);

    return (
        <div className="h-full w-full flex flex-col justify-between relative md:overflow-hidden bg-white dark:bg-[#1E1D21]">

            <div className="mt-0">
                <div className="flex flex-col p-4">
                    <img
                        alt="logo"
                        src={ccLogo}
                        className="h-16 w-16"
                    />
                    <p className="text-[22px] font-bold text-black dark:text-white mt-3">Log in to Chaa Chingg</p>
                    <p className="text-[14px] font-semibold text-gray-800 dark:text-gray-300">Welcome Back!</p>
                </div>
                <form className="w-full px-4" onSubmit={formik.handleSubmit}>
                    {(errorMessage || formik.errors.email) && (
                        <p className="text-[12px] font-bold text-red-500 mb-2">
                            {formik.errors.email ? formik.errors.email : errorMessage}
                        </p>
                    )}
                    <div className={`px-4 pt-4 pb-3 rounded-xl border-2 ${formik.errors.email && formik.touched.email ? 'border-red-500' : 'border-[#BDE6FF]'}`}>
                        <p className="text-[12px] font-semibold text-black dark:text-white">Email or phone number</p>
                        <div className="flex items-center gap-1">
                            {isPhoneIdentifierValue(formik.values.email) && (
                                <span className="text-[14px] text-black dark:text-white select-none">+1</span>
                            )}
                            <input
                                required
                                type="text"
                                name="email"
                                value={phoneLocalPart(formik.values.email)}
                                onChange={handleEmailOrPhoneChange}
                                onBlur={formik.handleBlur}
                                placeholder="Enter your email or phone number"
                                className="w-full text-[14px] focus:outline-none bg-white dark:bg-[#1E1D21] text-black dark:text-white"
                            />
                        </div>
                    </div>
                    <div className={`px-4 pt-4 pb-3 rounded-xl border-2 ${errorMessage ? 'border-red-500' : 'border-[#BDE6FF]'} mt-3`}>
                        <p className="text-[12px] font-semibold text-black dark:text-white">Password</p>
                        <div className="relative">
                            <input
                                required
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                placeholder="Enter your password"
                                className="w-full text-[14px] focus:outline-none bg-white dark:bg-[#1E1D21] text-black dark:text-white"
                            />
                            <p className="absolute top-2 z-50 right-2 cursor-pointer">
                                {showPassword ? (
                                    <HiEyeSlash
                                        className="text-gray-500"
                                        onClick={() => setShowPassword(!showPassword)}
                                    />
                                ) : (
                                    <HiMiniEye
                                        className="text-gray-500"
                                        onClick={() => setShowPassword(!showPassword)}
                                    />
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="flex justify-between items-center my-3 px-1">
                        <div className="flex items-center gap-2">
                            <label className="relative flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={keepMeLogin}
                                    onChange={handleChangeKeepLogin}
                                    className="sr-only peer"
                                />
                                <div className="h-4 w-4 border-2 border-gray-300 rounded-[5px] peer-checked:bg-gray-700 peer-checked:border-gray-700 flex items-center justify-center transition-all">
                                    {keepMeLogin && <HiCheck className="h-3 w-3 text-white" />}
                                </div>
                            </label>
                            <p className="text-[12px] text-gray-800 dark:text-gray-300">Keep me logged in</p>
                        </div>
                        <p
                            onClick={() => navigate("/resetpassword")}
                            className="text-[12px] font-semibold text-gray-800 dark:text-gray-300 cursor-pointer"
                        >
                            Forgot password?
                        </p>
                    </div>
                    <div className="w-full flex justify-center items-center flex-col mt-5">
                        <button
                            disabled={isLoading || !formik.values.email || !formik.values.password}
                            className={`w-full h-12 text-[16px] font-semibold rounded-xl transition-all ${isLoading || !formik.values.email || !formik.values.password
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-[#BDE6FF] text-gray-900'
                                }`}
                        >
                            {isLoading ? (
                                <BeatLoader
                                    size={10}
                                    color={"#000"}
                                    loading={isLoading}
                                />
                            ) : (
                                "Log in"
                            )}
                        </button>

                        <div className="w-full flex items-center gap-3 my-4">
                            <hr className="flex-1 border-gray-300 dark:border-gray-600" />
                            <span className="text-[11px] text-gray-400 font-medium">OR</span>
                            <hr className="flex-1 border-gray-300 dark:border-gray-600" />
                        </div>

                        <button
                            type="button"
                            onClick={initiateAppleLogin}
                            className="w-full h-12 flex items-center justify-center rounded-xl bg-black text-white text-[14px] font-semibold hover:bg-gray-900 transition-colors"
                        >
                            <AppleIcon />
                            Sign in with Apple
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate("/create-account-with")}
                            className="w-full h-12 mt-3 flex items-center justify-center rounded-xl bg-[#BDE6FF] hover:bg-[#b2e0fc] text-gray-900 text-[14px] font-semibold"
                        >
                            Create Account
                        </button>
                    </div>
                </form>
            </div>
            <p className="text-[10px] text-center font-medium text-black dark:text-white mb-10">
                By continuing, you agree to our
                <span
                    onClick={() => navigate("/terms-of-use")}
                    className="cursor-pointer mx-1 text-blue-400 hover:text-blue-500 font-semibold"
                >
                    Terms of Use
                </span>
                and
                <span
                    onClick={() => navigate("/privacy-policy")}
                    className="cursor-pointer ml-1 text-blue-400 hover:text-blue-500 font-semibold"
                >
                    Privacy Policy
                </span>
            </p>
        </div>
    );
};

export default LoginPage;
